import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// --- Styled Components (Exact Image Match & Left Aligned) ---
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const Title = styled.h3`
  color: #004aad;
  font-size: 22px;
  font-weight: 400;
  margin: 0 0 8px 0;
`;

const Container = styled.div`
  background-color: #ececec;
  border-top: 3px solid #004aad;
  padding: 24px;
  width: 520px;
  max-width: 100%;
  border-radius: 2px;
  box-sizing: border-box;

  /* Small screen ke liye */
  @media (max-width: 768px) {
    width: 100%;
    padding: 16px 12px;
  }
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 14px;

  /* Small screen ke liye */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
`;

const Label = styled.label`
  font-weight: 700;
  font-size: 15px;
  color: #111;
  width: 170px;
  flex-shrink: 0;

  /* Small screen ke liye */
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Required = styled.span`
  color: red;
  margin-left: 3px;
`;

const InputWrapper = styled.div`
  flex-grow: 1;

  /* Small screen ke liye */
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 14px;
  height: 38px;
  box-sizing: border-box;
  background-color: #ffffff;
  color: #333;
`;

const Select = styled.select`
  width: 100%;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  font-size: 14px;
  height: 38px;
  box-sizing: border-box;
  background-color: #ffffff;
  color: #333;
`;

const ButtonRow = styled.div`
  padding-left: 170px;
  margin-top: 22px;

  /* Small screen ke liye */
  @media (max-width: 768px) {
    padding-left: 0;
    width: 100%;
  }
`;

const RunButton = styled.button`
  background-color: #004aad;
  color: white;
  padding: 7px 18px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #004aad;
  }

  /* Small screen ke liye */
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const AlertBox = styled.div`
  background-color: #ffe6e6;
  color: #d8000c;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  font-weight: bold;
  font-size: 14px;
  border: 1px solid #ffd2d2;
`;

// --- Current Month Dates ---
const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);

  const format = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  return { start: format(start), end: format(end) };
};

const CasesCancelledReportForm = () => {
  const defaultDates = getCurrentMonthDates();

  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);
  const [format, setFormat] = useState('On Screen');
  const [dateError, setDateError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const current = getCurrentMonthDates();
    setStartDate(current.start);
    setEndDate(current.end);
  }, []);

  const handleDateChange = (field, value) => {
    setDateError("");

    if (value === "") {
      if (field === "start") setStartDate(value);
      if (field === "end") setEndDate(value);
      return;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      setDateError("Baraye meharbani calendar icon par click karke tareekh chunein");
      return;
    }

    if (field === "start") setStartDate(value);
    if (field === "end") setEndDate(value);
  };

  const handleRun = () => {
    if (!startDate || !endDate) {
      setDateError("Start Date aur End Date dono darkaar hain");
      return;
    }

    navigate('/cases-cancelled-report', {
      state: { startDate, endDate, format }
    });
  };

  return (
    <Wrapper>
      <Title>Parameters</Title>

      <Container>
        {dateError && <AlertBox>{dateError}</AlertBox>}

        {/* Start Date */}
        <FormRow>
          <Label>
            Start Date<Required>*</Required>
          </Label>
          <InputWrapper>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => handleDateChange("start", e.target.value)}
            />
          </InputWrapper>
        </FormRow>

        {/* End Date */}
        <FormRow>
          <Label>
            End Date<Required>*</Required>
          </Label>
          <InputWrapper>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => handleDateChange("end", e.target.value)}
            />
          </InputWrapper>
        </FormRow>

        {/* Format Dropdown */}
        <FormRow>
          <Label>
            Format<Required>*</Required>
          </Label>
          <InputWrapper>
            <Select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option>On Screen</option>
              <option>PDF</option>
              <option>Excel</option>
            </Select>
          </InputWrapper>
        </FormRow>

        {/* Run Button */}
        <ButtonRow>
          <RunButton onClick={handleRun}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
            Run
          </RunButton>
        </ButtonRow>
      </Container>
    </Wrapper>
  );
};

export default CasesCancelledReportForm;